import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const COOKIE_CONSENT_KEY = "wellnessconnect_cookie_consent";
const COOKIE_CONSENT_VERSION = "1.0";

interface ConsentState {
  accepted: boolean;
  version: string;
  timestamp: string;
}

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      setShowBanner(true);
    } else {
      try {
        const consent: ConsentState = JSON.parse(stored);
        // Show banner if version changed or not accepted
        if (consent.version !== COOKIE_CONSENT_VERSION || !consent.accepted) {
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  const handleAccept = async () => {
    const consent: ConsentState = {
      accepted: true,
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);

    // Log consent to privacy_audit_logs
    try {
      await supabase.from("privacy_audit_logs").insert({
        event_type: "cookie_consent_accepted",
        email: user?.email || "anonymous",
        user_id: user?.id || null,
        consent_version: COOKIE_CONSENT_VERSION,
        user_agent: navigator.userAgent,
        details: {
          accepted: true,
          timestamp: consent.timestamp,
        },
      });
    } catch (error) {
      console.error("Failed to log cookie consent:", error);
    }
  };

  const handleDecline = async () => {
    const consent: ConsentState = {
      accepted: false,
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);

    // Log decline to privacy_audit_logs
    try {
      await supabase.from("privacy_audit_logs").insert({
        event_type: "cookie_consent_declined",
        email: user?.email || "anonymous",
        user_id: user?.id || null,
        consent_version: COOKIE_CONSENT_VERSION,
        user_agent: navigator.userAgent,
        details: {
          accepted: false,
          timestamp: consent.timestamp,
        },
      });
    } catch (error) {
      console.error("Failed to log cookie consent decline:", error);
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-card border-t border-border p-4 shadow-lg">
      <div className="container mx-auto max-w-6xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground flex-1">
          <p>
            We use cookies to enhance your experience, analyse site traffic, and for marketing purposes.
            By clicking "Accept", you consent to our use of cookies.{" "}
            <a href="/privacy" className="text-primary hover:underline">
              Learn more in our Privacy Policy
            </a>
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" onClick={handleDecline}>
            Decline
          </Button>
          <Button size="sm" onClick={handleAccept}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
